import * as React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';

export interface ModalProps {
  id?: string;
  children?: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  closeModal?: () => void;
  isSmall?: boolean;
  closeOnOutsideClick?: boolean;
}

const Modal: React.FC = (props: ModalProps) => {
  const {
    id,
    children,
    className,
    closeModal,
    isSmall,
    isOpen = false,
    ariaLabelledby,
    ariaDescribedby,
    closeOnOutsideClick,
  } = props;
  const modalRef = React.useRef<HTMLDivElement>(null);
  const modalClassNames = classNames(
    'ui-modal',
    { 'ui-modal--show': isOpen },
    className
  );
  const modalLockClassNames = classNames('ui-modal-lock', {
    'ui-modal-lock--show': isOpen,
  });

  const modalContentClass = classNames('ui-modal-content', {
    'ui-modal-content--show': isOpen,
    'ui-modal-content--small': isSmall,
  });

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeModal) {
        closeModal();
      }
    },
    [closeModal]
  );
  const handleClickOutside = React.useCallback(
    (event) => {
      if (
        modalRef &&
        !modalRef?.current?.contains(event.target) &&
        closeModal
      ) {
        closeModal();
      }
    },
    [modalRef, closeModal]
  );
  const getModalContent = () => (
    <div
      role="dialog"
      ref={modalRef}
      className={modalContentClass}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      {children}
    </div>
  );

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);

      if (closeOnOutsideClick) {
        document.addEventListener('mousedown', handleClickOutside);
      }
    }

    if (!isOpen) {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown, false);

      if (closeOnOutsideClick) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [isOpen, closeOnOutsideClick, handleClickOutside, handleKeyDown]);

  return ReactDOM.createPortal(
    isOpen ? (
      <div id={id} role="presentation" className={modalClassNames}>
        <FocusLock className={modalLockClassNames} returnFocus>
          {getModalContent()}
        </FocusLock>
      </div>
    ) : null,
    document.body
  );
};

export default Modal;
